import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/NavBar.css';

const NavBar = ({ activeTab, onTabChange, isLoggedIn, onLogout, onLogin, onProfileClick, marketState }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (path, tabId, requiresAuth = false) => {
    if (requiresAuth && !isLoggedIn) {
      sessionStorage.setItem('redirectAfterLogin', path);
      onLogin();
      setMenuOpen(false);
      return;
    }
    onTabChange(tabId);
    navigate(path);
    setMenuOpen(false);
  };

  const getCurrentPageName = () => {
    const path = location.pathname;
    if (path === '/') return 'Main Page';
    if (path === '/home') return 'Home';
    if (path.startsWith('/market')) return 'Market';
    if (path.startsWith('/stock')) return 'Stock';
    if (path.startsWith('/portfolio')) return 'Portfolio';
    if (path.startsWith('/events')) return 'Events';
    if (path.startsWith('/leaderboard')) return 'Leaderboard';
    if (path.startsWith('/overview')) return 'Overview';
    if (path.startsWith('/admin')) return 'Admin';
    if (path === '/games') return 'Games';
    if (path.startsWith('/games/')) return 'Games';
    if (path === '/broadcast') return 'Broadcast';
    if (path === '/videos') {
      const section = new URLSearchParams(location.search).get('section');
      if (section === 'music') return 'Music';
      if (section === 'commercials') return 'Commercials';
      return 'Videos';
    }
    if (path === '/workshop' || path === '/hackerspace') return 'Workshop';
    if (path === '/calendar' || path === '/upcoming') return 'Calendar';
    if (path === '/faq' || path === '/info') return 'FAQ';
    return '';
  };

  const primaryItems = [
    { path: '/', id: 'main', label: 'Main Page', icon: '🎬' },
  ];

  const mediaItems = [
    { path: '/videos', id: 'videos', label: 'Videos', icon: '🎬' },
    { path: '/videos?section=music', id: 'music', label: 'Music', icon: '🎵' },
    { path: '/broadcast', id: 'broadcast', label: 'Live Broadcast', icon: '📺' },
  ];

  const buildItems = [
    { path: '/calendar', id: 'calendar', label: 'Upcoming', icon: '📅' },
    { path: '/workshop', id: 'workshop', label: 'Workshop', icon: '🛠️' },
  ];

  /* Legacy Party III items — kept available but demoted below */
  const partyItems = [
    { path: '/home', id: 'home', label: 'Home', icon: '🏠' },
    { path: '/market', id: 'market', label: 'Market', icon: '📈' },
    { path: '/portfolio', id: 'portfolio', label: 'Portfolio', icon: '💼', auth: true },
    { path: '/leaderboard', id: 'leaderboard', label: 'Leaderboard', icon: '🏆' },
    { path: '/events', id: 'events', label: 'Events', icon: '📅' },
    { path: '/overview', id: 'overview', label: 'Overview', icon: '⏱️' },
    { path: '/faq', id: 'faq', label: 'How to Play / FAQ', icon: '❓' },
  ];

  const gameItems = [
    { path: '/games', id: 'games', label: 'Games', icon: '🎮' },
  ];

  const handleProfileClick = () => {
    if (isLoggedIn) {
      navigate('/portfolio');
    } else {
      onLogin();
    }
  };

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      {/* Fixed top bar - never moves content */}
      <header className="topbar">
        <div className="topbar-left">
          <button
            className="topbar-menu"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>

        <div className="topbar-center">
          {location.pathname.startsWith('/market') && (
            <img src="/komok_logo.png" alt="KOMOK" className="topbar-logo" />
          )}
          <span className="topbar-title">{getCurrentPageName()}</span>
        </div>

        <div className="topbar-right">
          {isLoggedIn ? (
            <button className="topbar-user" onClick={handleProfileClick}>Portfolio</button>
          ) : (
            <button className="topbar-user" onClick={onLogin}>Login</button>
          )}
        </div>
      </header>

      {/* Overlay menu - slides down, doesn't push content */}
      <div className={`menu-overlay ${menuOpen ? 'open' : ''}`} onClick={closeMenu}>
        <nav className="menu-panel" onClick={e => e.stopPropagation()}>
          {/* === TOP: primary, media, workshop === */}
          <div className="menu-nav-list">
            {primaryItems.map(item => (
              <button
                key={item.id}
                className={`menu-nav-item ${location.pathname === item.path ? 'active' : ''}`}
                onClick={() => handleNavigation(item.path, item.id, item.auth)}
              >
                <span className="menu-nav-icon">{item.icon}</span>
                <span className="menu-nav-label">{item.label}</span>
              </button>
            ))}
          </div>

          <div className="menu-divider" />

          <div className="menu-section-title">Media</div>
          <div className="menu-nav-list menu-nav-list-compact">
            {mediaItems.map(item => {
              const [itemPath, itemQuery] = item.path.split('?');
              const itemSection = itemQuery ? new URLSearchParams(itemQuery).get('section') : null;
              const currentSection = new URLSearchParams(location.search).get('section');
              const isActive = itemSection
                ? location.pathname === itemPath && currentSection === itemSection
                : location.pathname === itemPath && !currentSection;
              return (
                <button
                  key={item.id}
                  className={`menu-nav-item ${isActive ? 'active' : ''}`}
                  onClick={() => handleNavigation(item.path, item.id)}
                >
                  <span className="menu-nav-icon">{item.icon}</span>
                  <span className="menu-nav-label">{item.label}</span>
                </button>
              );
            })}
          </div>

          <div className="menu-divider" />

          <div className="menu-section-title">Happening</div>
          <div className="menu-nav-list menu-nav-list-compact">
            {buildItems.map(item => (
              <button
                key={item.id}
                className={`menu-nav-item ${location.pathname === item.path ? 'active' : ''}`}
                onClick={() => handleNavigation(item.path, item.id)}
              >
                <span className="menu-nav-icon">{item.icon}</span>
                <span className="menu-nav-label">{item.label}</span>
              </button>
            ))}
          </div>

          <div className="menu-divider menu-divider-major" />

          {/* === BOTTOM: legacy Party III stuff === */}
          <div className="menu-section-title menu-section-title-muted">Party III · Archive</div>
          <div className="menu-nav-list menu-nav-list-compact menu-nav-list-muted">
            {partyItems
              .filter(item => !item.hideIfGuest || isLoggedIn)
              .map(item => (
                <button
                  key={item.id}
                  className={`menu-nav-item ${location.pathname === item.path ? 'active' : ''}`}
                  onClick={() => handleNavigation(item.path, item.id, item.auth)}
                >
                  <span className="menu-nav-icon">{item.icon}</span>
                  <span className="menu-nav-label">{item.label}</span>
                </button>
              ))}
          </div>

          <div className="menu-section-title menu-section-title-muted">Games</div>
          <div className="menu-nav-list menu-nav-list-compact menu-nav-list-muted">
            {gameItems.map(item => (
              <button
                key={item.id}
                className={`menu-nav-item ${location.pathname === item.path || location.pathname.startsWith(item.path + '/') ? 'active' : ''}`}
                onClick={() => handleNavigation(item.path, item.id)}
              >
                <span className="menu-nav-icon">{item.icon}</span>
                <span className="menu-nav-label">{item.label}</span>
              </button>
            ))}
          </div>

          <div className="menu-divider" />

          <div className="menu-footer">
            {isLoggedIn ? (
              <button className="menu-action-btn danger" onClick={() => { onLogout(); closeMenu(); }}>
                Logout
              </button>
            ) : (
              <button className="menu-action-btn primary" onClick={() => { onLogin(); closeMenu(); }}>
                Login / Register
              </button>
            )}
          </div>
        </nav>
      </div>
    </>
  );
};

export default NavBar;
